import { styled } from '@mui/material';
import { useViewport } from 'contexts/viewport';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import Box from 'shell/components/Box';
import Card from 'shell/components/Card';
import Dropdown from 'shell/components/Dropdown';
import IconButton from 'shell/components/IconButton';
import Stack from 'shell/components/Stack';
import Tag from 'shell/components/Tag';
import Text from 'shell/components/Text';

const InstructorAssignmentCard = forwardRef(
    (
        {
            assignment,
            disableOptions,
            isDuplicating,
            onClick,
            onClickEdit,
            onClickDuplicate,
            onClickUnpublish,
            onClickPublish,
            onClickDelete,
            ...rest
        },
        ref
    ) => {
        const { device } = useViewport();
        const { t } = useTranslation();

        const handleSelect = ({ key }) => {
            if (key === 'edit') return onClickEdit(assignment);
            if (key === 'duplicate') return onClickDuplicate(assignment);
            if (key === 'unpublish') return onClickUnpublish(assignment);
            if (key === 'publish') return onClickPublish(assignment);
            if (key === 'delete') return onClickDelete(assignment);
        };

        return (
            <RootBox ref={ref} {...rest}>
                <Card onClick={() => onClick(assignment)}>
                    <RootStack alignItems={'flex-start'} spacing={2}>
                        <CustomStack alignItems={'flex-end'} direction={'row'} justifyContent={'space-between'}>
                            <Text align={'left'} variant={'sh2'}>
                                {assignment.title}
                            </Text>
                        </CustomStack>
                        <Stack direction={device === 'mobile' ? 'column' : 'row'} spacing={2}>
                            {assignment.ai_grading_enabled && <Tag type={'ai'} value={'grading'} />}
                            {assignment.ai_feedback_enabled && <Tag type={'ai'} value={'feedback'} />}
                            {process.env.REACT_APP_AUTH !== 'PASSWORD' && assignment.ai_student_feedback_enabled && (
                                <Tag type={'ai'} value={'studentFeedback'} />
                            )}
                        </Stack>
                    </RootStack>
                    {assignment.is_draft ? (
                        <CustomText sx={{ fontStyle: 'italic' }} color={'light'}>
                            {t('status.draft', 'Draft')}
                        </CustomText>
                    ) : (
                        <CustomText color={'light'}>{t('status.published', 'Published')}</CustomText>
                    )}
                </Card>
                <CustomBox>
                    <Dropdown
                        id={`${assignment.id}-dropdown`}
                        options={[
                            { key: 'edit', value: t('controls.edit', 'Edit') },
                            { key: 'duplicate', value: t('controls.duplicate', 'Duplicate'), disabled: isDuplicating },
                            assignment.is_draft
                                ? { key: 'publish', value: t('controls.publish', 'Publish') }
                                : { key: 'unpublish', value: t('controls.unpublish', 'Unpublish') },
                            { key: 'delete', value: t('controls.delete', 'Delete') },
                        ]}
                        rootComponent={
                            <IconButton
                                aria-label={'more options'}
                                disabled={disableOptions}
                                kind={'moreHorizontal'}
                                size={'medium'}
                                variant={'square'}
                            />
                        }
                        onSelect={handleSelect}
                    />
                </CustomBox>
            </RootBox>
        );
    }
);

const RootBox = styled(Box)(() => {
    return {
        position: 'relative',
        width: '100%',
    };
});

const RootStack = styled(Stack)(({ theme }) => {
    return {
        width: '100%',
        padding: theme.spacing(2),
    };
});

const CustomStack = styled(Stack)(() => {
    return {
        width: 'calc(100% - 120px)',
    };
});

const CustomText = styled(Text)(({ theme }) => {
    return {
        position: 'absolute',
        top: theme.spacing(7),
        right: theme.spacing(16),
    };
});

const CustomBox = styled(Box)(({ theme }) => {
    return {
        position: 'absolute',
        top: theme.spacing(5),
        right: theme.spacing(5),
    };
});

export default InstructorAssignmentCard;
